import "./styles.css";
import type {
  ActivityCard,
  LessonEvent,
  LessonMode,
  LessonSectionId,
  LessonSessionState,
  VisualSpec,
} from "./contracts";
import { BundledTeacherVoice, createAnalysedAudioFactory } from "./bundledTeacherVoice";
import {
  createInitialSession,
  currentCard,
  durationForMode,
  transitionLesson,
} from "./lessonEngine";
import {
  COMPLETION_CUE_ID,
  feedbackCueId,
  R2_BUNDLED_AUDIO_MANIFEST,
  R2_VOICE_SAMPLE_CUE_ID,
  R2_VOICE_GATE_STATUS,
  teacherCueId,
} from "./r2AudioManifest";
import { ACTIVE_R2_DAY1_LESSON, R2_CONTENT_GATE_STATUS } from "./r2LessonRegistry";
import { STATE_AUDIO_CUES, STATE_VOICE_MANIFEST } from "./livingTeacher/stateVoiceAssets";
import { LivingAvatar, type TeacherState } from "./livingAvatar/livingAvatar";
import { UnifiedChildInput } from "./childInput/unifiedChildInput";
import {
  GOLDEN_SCENES,
  type GoldenInteractionType,
  type GoldenSceneId,
  type GoldenSceneSpec,
} from "./goldenLoop/goldenLessonData";
import {
  GoldenEngine,
  type GoldenLoopPhase,
  type GoldenSessionState,
} from "./goldenLoop/goldenEngine";

type AppExperienceMode = "GOLDEN_LOOP" | "FULL_R2" | "FAST_QA";

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) {
    throw new Error(`FAST_TRACK_DAY1 required element missing: ${selector}`);
  }
  return element;
}

function formatClock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const minutesPart = Math.floor(seconds / 60).toString().padStart(2, "0");
  const secondsPart = (seconds % 60).toString().padStart(2, "0");
  return `${minutesPart}:${secondsPart}`;
}

function renderApples(count: number, compact = false, highlightIndices: number[] = []): string {
  return `<div class="apple-row${compact ? " compact" : ""}" data-count="${count}" aria-label="사과 ${count}개">${Array.from(
    { length: count },
    (_, index) => {
      const isHighlighted = highlightIndices.includes(index);
      return `<span class="apple-prop${isHighlighted ? " scaffold-highlight" : ""}" data-item="${index + 1}" style="--apple-index:${index}" aria-hidden="true">
        <img src="/assets/day1-visuals/apple-v1.png" alt="사과" class="realistic-apple" draggable="false" />
      </span>`;
    },
  ).join("")}</div>`;
}

function renderHandVisual(count: number): string {
  const safe = Number.isFinite(count) ? Math.max(0, Math.min(10, Math.floor(count))) : 0;
  const left = Math.min(safe, 5);
  const right = Math.max(0, safe - 5);
  const poses = [left, right];
  return `<div class="hand-pair" role="img" aria-label="손가락 ${safe}개">${poses
    .map(
      (pose, i) =>
        `<span class="hand-piece"><span class="hand-sprite ${i ? "mirrored" : ""}" data-extended="${pose}" aria-hidden="true" style="--pose-x:${(pose % 3) * 50}%;--pose-y:${pose < 3 ? 0 : 100}%"></span><small>${pose}</small></span>`,
    )
    .join('<span class="hand-plus" aria-hidden="true">+</span>')}${right > 0 ? `<strong>= ${safe}</strong>` : ""}</div>`;
}

function renderVisual(visual: VisualSpec, highlightIndices: number[] = []): string {
  switch (visual.type) {
    case "WELCOME":
      return `<div class="welcome-visual"><span>✦</span><strong>${visual.accent === "GOODBYE" ? "다음에 또 만나요" : "반가워요!"}</strong><span>✦</span></div>`;
    case "FINGERS":
      return renderHandVisual(visual.count);
    case "APPLES":
      return renderApples(visual.count, false, highlightIndices);
    case "NUMBER":
      return `<div class="number-visual" role="img" aria-label="숫자 ${visual.value}">${visual.value}</div>`;
    case "NUMBER_LINE":
      return `<div class="number-line" role="img" aria-label="숫자 ${visual.from}부터 ${visual.to}까지">${Array.from(
        { length: visual.to - visual.from + 1 },
        (_, index) => {
          const value = visual.from + index;
          return `<span${visual.active === value ? ' data-active="true"' : ""}>${value}</span>`;
        },
      ).join("")}</div>`;
    case "BASKET":
      return `<div class="basket-visual" role="img" aria-label="바구니 사과 ${visual.revealed}개"><div>${renderApples(visual.revealed, true)}</div><span>🧺</span></div>`;
    case "COMPARE":
      return `<div class="compare-visual"><div>${renderApples(visual.left, true)}</div><div>VS</div><div>${renderApples(visual.right, true)}</div></div>`;
    case "CELEBRATE":
      return `<div class="celebrate-visual" aria-label="별 ${visual.stars}개">${Array.from({ length: visual.stars }, (_, index) => `<span style="--star-index:${index}">★</span>`).join("")}</div>`;
  }
}

const app = requiredElement<HTMLElement>("#app");
const urlParams = new URLSearchParams(window.location.search);
const requestedModeParam = urlParams.get("mode");

// DCR-CG-20260915-01: G1~G6 진입점 단일화 (28카드/40분 코스 런타임 차단)
let appMode: AppExperienceMode = "GOLDEN_LOOP";

app.innerHTML = `
  <div class="lesson-app" data-mode="${appMode}" data-phase="READY">
    <header class="topbar">
      <div class="brand">
        <span class="brand-mark">지나</span>
        <div><strong>지나쌤 숫자교실</strong><small id="app-subtitle">초급1 · 골든 인터랙션 루프</small></div>
      </div>
      <div class="session-meta">
        <span id="mode-badge">골든 루프 · 약 5분</span>
        <span id="session-clock">00:00</span>
      </div>
    </header>

    <!-- Golden Loop Track -->
    <nav id="golden-track" class="section-track" aria-label="골든 인터랙션 루프 씬">
      ${GOLDEN_SCENES.map((scene, index) => `
        <div class="section-step" data-scene="${scene.id}" data-index="${index}">
          <span>G${index + 1}</span>
          <div><small>${scene.id}</small><strong>${scene.title.split(" ")[0]}</strong></div>
        </div>`).join("")}
    </nav>

    <!-- R2 Full Track (Hidden by default) -->
    <nav id="r2-track" class="section-track" aria-label="오늘 수업 순서" hidden>
      <div class="section-step" data-section="WELCOME"><span>👋</span><div><small>1교시</small><strong>반가워요</strong></div></div>
      <div class="section-step" data-section="PLAY"><span class="step-icon"><img src="/assets/day1-visuals/apple-v1.png" class="step-apple-img" alt="" /></span><div><small>2교시</small><strong>숫자 놀이</strong></div></div>
      <div class="section-step" data-section="QUIZ"><span>⭐</span><div><small>3교시</small><strong>숫자 퀴즈</strong></div></div>
      <div class="section-step" data-section="CELEBRATION"><span>🎉</span><div><small>4교시</small><strong>칭찬 시간</strong></div></div>
    </nav>

    <section class="teacher-layout">
      <!-- Living Avatar Panel -->
      <aside class="teacher-panel" aria-label="살아있는 지나쌤 아바타">
        <div id="living-avatar-mount"></div>
      </aside>

      <!-- Activity Stage -->
      <main class="activity-stage" aria-live="polite">
        <div class="activity-heading">
          <div>
            <p id="section-kicker" class="section-kicker">살아있는 교실</p>
            <h1 id="activity-title">지나쌤과 숫자 1·2·3 첫 교감</h1>
          </div>
          <div class="card-counter"><strong id="card-number">1</strong><span id="card-total">/ 6</span></div>
        </div>

        <div id="visual-stage" class="visual-stage">
          <div class="start-visual"><span>1</span><span>2</span><span>3</span></div>
        </div>

        <blockquote id="teacher-bubble" class="teacher-bubble">
          <span class="bubble-name">지나쌤</span>
          <strong id="teacher-text">안녕? 난 지나쌤이야! 만나서 정말 반가워.</strong>
        </blockquote>

        <p id="activity-hint" class="activity-hint">목소리, 손뼉, 또는 큰 터치 버튼으로 교감해요.</p>

        <!-- Dynamic Toddler Touch Surface -->
        <div id="toddler-touch-surface" class="toddler-touch-grid" aria-label="유아용 대형 터치 버튼"></div>

        <!-- Feedback Banner -->
        <div id="golden-feedback" class="golden-feedback-banner" hidden>
          <span class="feedback-icon">★</span>
          <span class="feedback-msg">우와! 지나쌤이 들었어!</span>
        </div>

        <!-- R2 Full Lesson Answer Grid (Preserved) -->
        <div id="r2-answer-grid" class="answer-grid" aria-label="R2 큰 터치 답변" hidden></div>
        <div id="r2-feedback-panel" class="feedback-panel" hidden>
          <span id="feedback-icon">★</span>
          <strong id="feedback-text"></strong>
          <div class="feedback-actions">
            <button id="retry-card" type="button">다시 해볼게요</button>
            <button id="next-card" type="button">다음 활동</button>
          </div>
        </div>

        <div class="activity-footer">
          <div class="time-progress" aria-label="현재 활동 시간"><span id="time-progress-fill"></span></div>
          <div class="time-copy"><span>진행 시간</span><strong id="card-time">00:00</strong></div>
          <button id="manual-next" type="button" class="next-button">다음으로 →</button>
        </div>
      </main>
    </section>

    <!-- Unified Child Input Dock -->
    <section class="vad-dock" aria-label="스마트 음성 및 박수 감지기">
      <button id="toggle-mic" type="button"><span>🎤</span><strong>마이크 켜기</strong></button>
      <div class="vad-copy">
        <strong id="mic-status-label">마이크는 선택사항이에요</strong>
        <span id="mic-status-desc">말하기, 박수, 또는 큰 터치만으로 수업 전체를 끝낼 수 있어요.</span>
      </div>
      <div class="vad-meter" role="meter" aria-valuemin="0" aria-valuemax="1" aria-valuenow="0">
        <span id="vad-level"></span>
      </div>
    </section>

    <!-- Start Overlay -->
    <div id="start-overlay" class="start-overlay">
      <div class="start-card">
        <p>YES82 · 지나쌤 숫자교실 · 초급1</p>
        <h1>숫자 1·2·3 골든 인터랙션 루프</h1>
        <span>지나쌤이 살아 움직이고, 아이의 말과 행동에 정직하게 반응해요.</span>

        <div class="r2-gate-card" role="status">
          <strong>살아있는 교사 시스템 가동 준비</strong>
          <span id="voice-gate-state">BVE 실사 영상 연동 · 상호작용 경청 · 정직한 입력 소스 분리</span>
        </div>

        <div class="mode-picker" role="group" aria-label="수업 모드 선택">
          <button id="start-golden" type="button" class="primary-start" style="grid-column: 1 / -1;">
            <strong>골든 인터랙션 루프 시작하기</strong><small>만 24~48개월 · 5~7분 마이크로 세션 (G1 ~ G6)</small>
          </button>
        </div>
        <div style="margin: 10px 0; text-align: center;">
          <a href="/day1-living.html?mode=review" style="font-size: 13px; color: #ff765d; text-decoration: underline; font-weight: 600;">🛠️ 성인 전수 검토 모드 (28카드 전체 점검)</a>
        </div>
        <small class="safety-copy">BVE 실사 비디오 8종 체결 · 로컬 VAD/터치 전용 (Cloud 음성 전송 없음)</small>
      </div>
    </div>

    <!-- Complete Overlay -->
    <div id="complete-overlay" class="complete-overlay" hidden>
      <div class="complete-card">
        <div class="complete-stars">★ ★ ★ ★ ★</div>
        <p>초급1 · 골든 루프 완료</p>
        <h1>오늘 정말 최고였어!</h1>
        <span>지나쌤과 숫자 1, 2, 3 친구를 모두 즐겁게 만났어요.</span>
        <button id="restart-lesson" type="button">처음부터 다시 만나기</button>
      </div>
    </div>
  </div>`;

// DOM Element References
const lessonApp = requiredElement<HTMLElement>(".lesson-app");
const modeBadge = requiredElement<HTMLElement>("#mode-badge");
const sessionClock = requiredElement<HTMLElement>("#session-clock");
const sectionKicker = requiredElement<HTMLElement>("#section-kicker");
const activityTitle = requiredElement<HTMLElement>("#activity-title");
const cardNumber = requiredElement<HTMLElement>("#card-number");
const cardTotal = requiredElement<HTMLElement>("#card-total");
const visualStage = requiredElement<HTMLElement>("#visual-stage");
const teacherText = requiredElement<HTMLElement>("#teacher-text");
const activityHint = requiredElement<HTMLElement>("#activity-hint");
const toddlerTouchSurface = requiredElement<HTMLElement>("#toddler-touch-surface");
const goldenFeedback = requiredElement<HTMLElement>("#golden-feedback");
const startOverlay = requiredElement<HTMLElement>("#start-overlay");
const completeOverlay = requiredElement<HTMLElement>("#complete-overlay");
const startGoldenButton = requiredElement<HTMLButtonElement>("#start-golden");
const restartLessonButton = requiredElement<HTMLButtonElement>("#restart-lesson");
const manualNextButton = requiredElement<HTMLButtonElement>("#manual-next");
const toggleMicButton = requiredElement<HTMLButtonElement>("#toggle-mic");
const micStatusLabel = requiredElement<HTMLElement>("#mic-status-label");
const micStatusDesc = requiredElement<HTMLElement>("#mic-status-desc");
const vadLevel = requiredElement<HTMLElement>("#vad-level");
const goldenTrack = requiredElement<HTMLElement>("#golden-track");
const r2Track = requiredElement<HTMLElement>("#r2-track");

// Initialize Audio & Analyser
const { factory: audioFactory, getAnalyser } = createAnalysedAudioFactory();
const ALL_AUDIO_MANIFEST = Object.freeze({
  ...R2_BUNDLED_AUDIO_MANIFEST,
  ...STATE_VOICE_MANIFEST,
});
const voice = new BundledTeacherVoice(ALL_AUDIO_MANIFEST, audioFactory);

// Initialize Living Avatar
const avatarMount = requiredElement<HTMLElement>("#living-avatar-mount");
const avatar = new LivingAvatar({ container: avatarMount, initialState: "IDLE" });
avatar.attachAudioAnalyser(getAnalyser());

// Clock timer
let sessionStartedAtMs = 0;
let clockTimer: number | null = null;

function startClock(): void {
  sessionStartedAtMs = performance.now();
  if (clockTimer !== null) clearInterval(clockTimer);
  clockTimer = window.setInterval(() => {
    const elapsedSec = (performance.now() - sessionStartedAtMs) / 1000;
    sessionClock.textContent = formatClock(elapsedSec);
  }, 1000);
}

// Unified Child Input
let childInput: UnifiedChildInput;
let goldenEngine: GoldenEngine;

childInput = new UnifiedChildInput({
  onVoiceActivity(rms) {
    const pct = Math.min(100, Math.round(rms * 400));
    vadLevel.style.width = `${pct}%`;
  },
  onAcknowledge(latencyMs) {
    goldenEngine.handleAcknowledge(latencyMs, "VOICE");
  },
  onParticipationDetected() {
    goldenEngine.handleParticipation("VOICE");
  },
  onClapDetected(latencyMs) {
    if (goldenEngine.state.phase === "TEACHER_LISTENING") {
      goldenEngine.handleAcknowledge(latencyMs, "CLAP");
    } else {
      goldenEngine.handleParticipation("CLAP");
    }
  },
  onSemanticResult(decision, _transcript, latencies) {
    goldenEngine.handleSemanticDecision(decision, "VOICE", latencies);
  },
  onSilenceTimeout() {
    goldenEngine.handleSilenceTimeout();
  },
  onMicPermissionChanged(granted, reason) {
    if (granted) {
      toggleMicButton.innerHTML = "<span>🔴</span><strong>마이크 켜짐</strong>";
      micStatusLabel.textContent = "목소리와 박수를 듣고 있어요";
      micStatusDesc.textContent = "지나쌤에게 이야기하거나 박수를 쳐보세요!";
    } else {
      toggleMicButton.innerHTML = "<span>🎤</span><strong>마이크 켜기</strong>";
      micStatusLabel.textContent = "터치 모드로 진행 중";
      micStatusDesc.textContent = reason === "PERMISSION_DENIED"
        ? "마이크 권한이 꺼져 있어요. 화면의 큰 버튼으로 답해주세요."
        : "터치만으로도 즐겁게 수업할 수 있어요.";
    }
  },
});

toggleMicButton.addEventListener("click", async () => {
  if (!childInput.isMicGranted) {
    await childInput.initMicrophone();
  }
});

// Setup Golden Engine
goldenEngine = new GoldenEngine({
  onStateChanged(state) {
    renderGoldenState(state);
  },
  onPlayTeacherAudio(cueId, onEnd) {
    childInput.setTeacherSpeaking(true);
    avatar.setState("SPEAKING");

    voice.play(cueId, {
      onEnd() {
        childInput.setTeacherSpeaking(false);
        onEnd();
      },
      onError() {
        childInput.setTeacherSpeaking(false);
        onEnd();
      },
      onUnavailable() {
        childInput.setTeacherSpeaking(false);
        onEnd();
      },
    });
  },
  onPlayAcknowledgeAudio(onEnd, source) {
    if (source === "TOUCH") {
      // Q03: 터치 입력 시 "들었어" 음원 재생 금지 (터치 전용 음원 자산 결손 상태).
      // 시각적 고개 끄덕임(ACKNOWLEDGE 상태 비디오) 후 자연스럽게 호흡 간격으로 연결.
      setTimeout(onEnd, 300);
      return;
    }
    childInput.setTeacherSpeaking(true);
    voice.play(STATE_AUDIO_CUES.ACKNOWLEDGE, {
      onEnd() {
        childInput.setTeacherSpeaking(false);
        onEnd();
      },
      onError() {
        childInput.setTeacherSpeaking(false);
        onEnd();
      },
      onUnavailable() {
        childInput.setTeacherSpeaking(false);
        onEnd();
      },
    });
  },
  onPlayFeedbackAudio(type, onEnd) {
    childInput.setTeacherSpeaking(true);
    const cue =
      type === "CORRECT"
        ? STATE_AUDIO_CUES.PRAISE
        : type === "WRONG"
          ? STATE_AUDIO_CUES.RETRY
          : STATE_AUDIO_CUES.WAIT;
    voice.play(cue, {
      onEnd() {
        childInput.setTeacherSpeaking(false);
        onEnd();
      },
      onError() {
        childInput.setTeacherSpeaking(false);
        onEnd();
      },
      onUnavailable() {
        childInput.setTeacherSpeaking(false);
        onEnd();
      },
    });
  },
});

function updateGoldenTrack(activeSceneId: GoldenSceneId): void {
  const steps = goldenTrack.querySelectorAll<HTMLElement>(".section-step");
  const sceneIdx = GOLDEN_SCENES.findIndex((s) => s.id === activeSceneId);
  steps.forEach((step, idx) => {
    step.dataset.state = idx < sceneIdx ? "done" : idx === sceneIdx ? "active" : "upcoming";
  });
}

function renderGoldenState(state: GoldenSessionState): void {
  const scene = state.currentScene;
  if (state.phase === "CHILD_ACKNOWLEDGED") {
    const src = state.inputSource ?? "VOICE";
    const badgeMsg =
      src === "TOUCH"
        ? "응! 터치를 확인했어 👆"
        : src === "CLAP"
          ? "짝짝! 박수 소리를 들었어 👏"
          : "응! 지나쌤이 들었어 👂";
    avatar.setState(state.teacherState, badgeMsg);
  } else {
    avatar.setState(state.teacherState);
  }

  if (state.phase === "SESSION_COMPLETE") {
    completeOverlay.hidden = false;
    voice.play(COMPLETION_CUE_ID);
    return;
  }

  // Update headers
  sectionKicker.textContent = `골든 씬 · ${scene.id} (${scene.pedagogicalGoal})`;
  activityTitle.textContent = scene.title;
  cardNumber.textContent = String(state.sceneIndex + 1);
  cardTotal.textContent = `/ ${GOLDEN_SCENES.length}`;
  updateGoldenTrack(scene.id);

  // Q02: Scaffold Highlight per scene
  const highlightIndices =
    state.phase === "SCAFFOLD_HINT"
      ? scene.id === "G4"
        ? [0, 1, 2]
        : scene.id === "G5"
          ? [0, 1]
          : []
      : [];
  visualStage.innerHTML = renderVisual(scene.visual, highlightIndices);

  if (state.phase === "TEACHER_SPEAKING") {
    teacherText.textContent = scene.teacherSpeech;
    activityHint.textContent = "지나쌤이 이야기하고 있어요. 귀를 기울여 봐요!";
    toddlerTouchSurface.innerHTML = "";
    goldenFeedback.hidden = true;
    childInput.setMode("IDLE");
  } else if (state.phase === "TEACHER_LISTENING") {
    teacherText.textContent = scene.repromptText;
    activityHint.textContent = scene.hintText;
    goldenFeedback.hidden = true;

    // Configure Child Input Mode
    if (scene.interactionType === "VOICE_IMITATION" || scene.interactionType === "GREETING_PARTICIPATION") {
      childInput.setMode("PARTICIPATION", undefined, 6000);
    } else if (scene.interactionType === "CLAP_ACTION") {
      childInput.setMode("CLAP", undefined, 6000);
    } else if (scene.interactionType === "SEMANTIC_NUMBER") {
      childInput.setMode("SEMANTIC", scene.expectedNumber ?? 3, 7000);
    } else if (scene.interactionType === "SILENCE_SCAFFOLD") {
      childInput.setMode("SEMANTIC", scene.expectedNumber ?? 2, 4000);
    } else {
      childInput.setMode("PARTICIPATION", undefined, 6000);
    }

    renderToddlerButtons(scene);
  } else if (state.phase === "CHILD_ACKNOWLEDGED") {
    goldenFeedback.hidden = false;
    goldenFeedback.dataset.type = "WAIT";
    const src = state.inputSource ?? "VOICE";
    const srcTag = src === "TOUCH" ? "[입력: 터치]" : src === "CLAP" ? "[입력: 박수]" : "[입력: 음성]";
    const tAText = state.latencies.tA !== null && src !== "TOUCH" ? `${state.latencies.tA}ms` : "N/A · 미측정";
    const msg =
      src === "TOUCH"
        ? "응! 지나쌤이 확인했어"
        : src === "CLAP"
          ? "짝짝! 박수 소리를 들었어…"
          : "응! 지나쌤이 들었어…";
    const icon = src === "CLAP" ? "👏" : src === "TOUCH" ? "👆" : "👂";
    goldenFeedback.innerHTML = `<span class="feedback-icon">${icon}</span><strong class="feedback-msg">${srcTag} ${msg} (T-A: ${tAText})</strong>`;
    teacherText.textContent = src === "TOUCH" ? "응! 화면을 콕 눌렀구나!" : "응! 지나쌤이 듣고 있어!";
  } else if (state.phase === "HONEST_REACTION") {
    const record = state.lastRecord;
    goldenFeedback.hidden = false;
    const src = state.inputSource ?? (record && "modality" in record ? record.modality : "VOICE");
    const srcTag = src === "TOUCH" ? "[입력: 터치]" : src === "CLAP" ? "[입력: 박수]" : "[입력: 음성]";
    const tAText = state.latencies.tA !== null && src !== "TOUCH" ? ` (T-A: ${state.latencies.tA}ms)` : "";
    if (record?.type === "PARTICIPATED") {
      goldenFeedback.dataset.type = "CORRECT";
      const bodyMsg =
        src === "TOUCH"
          ? "우와! 지나쌤이랑 함께 했구나! 최고야!"
          : src === "CLAP"
            ? "짝짝! 신나게 박수 쳤구나! 최고야!"
            : "우와! 지나쌤이 똑똑히 들었어! 최고야!";
      goldenFeedback.innerHTML = `<span class="feedback-icon">✨</span><strong class="feedback-msg">${srcTag} ${bodyMsg}${tAText}</strong>`;
    } else if (record?.type === "SEMANTIC_DECISION") {
      if (record.decision.verdict === "CORRECT") {
        goldenFeedback.dataset.type = "CORRECT";
        goldenFeedback.innerHTML = `<span class="feedback-icon">★</span><strong class="feedback-msg">${srcTag} 맞아! 사과가 ${record.decision.number}개야!${tAText}</strong>`;
      } else if (record.decision.verdict === "WRONG") {
        goldenFeedback.dataset.type = "RETRY";
        goldenFeedback.innerHTML = `<span class="feedback-icon">↻</span><strong class="feedback-msg">${srcTag} 어? 다시 한 번 천천히 세어볼까?${tAText}</strong>`;
      } else {
        goldenFeedback.dataset.type = "WAIT";
        goldenFeedback.innerHTML = `<span class="feedback-icon">🌱</span><strong class="feedback-msg">${srcTag} 한 번만 더 말해줄래? 큰 버튼을 눌러도 좋아!</strong>`;
      }
    }
  } else if (state.phase === "SCAFFOLD_HINT") {
    goldenFeedback.hidden = false;
    goldenFeedback.dataset.type = "WAIT";

    // Q02: 카드·상태별 맞춤 힌트 매핑 (G1/G3/G6에 사과 힌트 금지, G4 사과 셋 힌트 일치)
    let hintMsg = "지나쌤이랑 같이 해보자!";
    let speechMsg = scene.repromptText;

    switch (scene.id) {
      case "G1":
        hintMsg = "지나쌤에게 손을 흔들거나 '안녕' 해볼까?";
        speechMsg = "안녕하고 인사해 줄래?";
        break;
      case "G2":
        hintMsg = "손가락 하나를 펴거나 '하나!' 외쳐볼까?";
        speechMsg = "지나쌤처럼 '하나!' 해볼까?";
        break;
      case "G3":
        hintMsg = "손뼉을 짝짝! 두 번 쳐볼까?";
        speechMsg = "지나쌤이랑 같이 박수 쳐볼까? 짝! 짝!";
        break;
      case "G4":
        hintMsg = "지나쌤이랑 같이 세어볼까? 하나, 둘, 셋!";
        speechMsg = "사과가 하나, 둘, 그리고 셋! 몇 개일까?";
        break;
      case "G5":
        hintMsg = "지나쌤이랑 같이 보자! 사과가 하나… 둘!";
        speechMsg = "사과가 하나… 그리고 둘! 몇 개일까?";
        break;
      case "G6":
        hintMsg = "오늘 열심히 한 나에게 큰 박수를 보내요!";
        speechMsg = "다음에 또 만나요!";
        break;
      default:
        hintMsg = scene.hintText;
        speechMsg = scene.repromptText;
        break;
    }

    goldenFeedback.innerHTML = `<span class="feedback-icon">💡</span><strong class="feedback-msg">${hintMsg}</strong>`;
    teacherText.textContent = speechMsg;
    activityHint.textContent = hintMsg;

    // Q05: 힌트 중에도 아동의 즉각적인 선택 입력 허용 및 가시성 보장
    renderToddlerButtons(scene);
  }
}

function renderToddlerButtons(scene: GoldenSceneSpec): void {
  toddlerTouchSurface.innerHTML = "";

  if (scene.interactionType === "SEMANTIC_NUMBER" || scene.interactionType === "SILENCE_SCAFFOLD") {
    // High-contrast large toddler buttons: 1, 2, 3
    const choices = [1, 2, 3] as const;
    choices.forEach((num) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "toddler-touch-btn";
      const appleIcons = Array.from(
        { length: num },
        () => `<img src="/assets/day1-visuals/apple-v1.png" class="btn-apple-icon" alt="" draggable="false" />`,
      ).join("");
      btn.innerHTML = `<span class="btn-visual btn-apples-${num}">${appleIcons}</span><strong class="btn-label">${num}</strong>`;
      btn.addEventListener("click", () => {
        const isMatch = num === scene.expectedNumber;
        goldenEngine.handleAcknowledge("TOUCH");
        setTimeout(() => {
          goldenEngine.handleSemanticDecision(
            {
              verdict: isMatch ? "CORRECT" : "WRONG",
              number: num,
              reason: "TODDLER_TOUCH_CHOICE",
            },
            "TOUCH",
          );
        }, 1200);
      });
      toddlerTouchSurface.appendChild(btn);
    });
  } else {
    // Single large friendly touch surface
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "toddler-touch-btn primary";
    btn.innerHTML = `<span class="btn-visual">${scene.visual.type === "CELEBRATE" ? "⭐" : "🙌"}</span><strong class="btn-label">${scene.touchLabel}</strong>`;
    btn.addEventListener("click", () => {
      goldenEngine.handleAcknowledge("TOUCH");
    });
    toddlerTouchSurface.appendChild(btn);
  }
}

// Manual next as emergency rescue
manualNextButton.addEventListener("click", () => {
  if (appMode === "GOLDEN_LOOP") {
    goldenEngine.handleParticipation("TOUCH");
  }
});

// Mode Start Handlers
startGoldenButton.addEventListener("click", async () => {
  appMode = "GOLDEN_LOOP";
  lessonApp.dataset.mode = "GOLDEN_LOOP";
  startOverlay.hidden = true;
  goldenTrack.hidden = false;
  r2Track.hidden = true;
  startClock();
  // Request microphone permission on user interaction
  await childInput.initMicrophone();
  goldenEngine.start();
});

restartLessonButton.addEventListener("click", () => {
  completeOverlay.hidden = true;
  // Q07: 수업 경과 시간(00:00) 초기화
  startClock();
  sessionClock.textContent = "00:00";
  if (appMode === "GOLDEN_LOOP") {
    goldenEngine.restart();
  }
});

goldenTrack.querySelectorAll<HTMLElement>(".section-step").forEach((step) => {
  step.style.cursor = "pointer";
  step.addEventListener("click", () => {
    const idx = Number(step.dataset.index);
    if (!Number.isNaN(idx) && appMode === "GOLDEN_LOOP") {
      goldenEngine.jumpToScene(idx);
    }
  });
});


if (typeof window !== "undefined") {
  (window as any).__GOLDEN_ENGINE__ = goldenEngine;
  (window as any).__CHILD_INPUT__ = childInput;
}
